import { tv, type VariantProps } from "@ui/lib/tv";

/** Desktop header row (mobile renders the sheet title bar / TopBarMobile instead). */
export const surfaceHeaderRow = tv({
  base: "hidden shrink-0 items-center justify-between border-neutral-100 border-b pt-5 pb-4 md:flex",
});

export const surfaceCloseButton = tv({
  base: "relative -me-2 -mt-2 flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full text-neutral-600 outline-none hover:text-neutral-900 focus-visible:ring-2 focus-visible:ring-neutral-400",
});

/**
 * Mobile sheet header. Default: a title bar at the sheet's top edge —
 * centered title, a nav control (close X trailing, or back arrow leading)
 * and a bottom hairline. With the Dialog's `grabber` on, the grabber is the
 * dismissal affordance instead: the nav control is dropped and the bar sits
 * compactly below the grabber band without the hairline.
 */
export const sheetHeader = tv({
  defaultVariants: {
    grabber: false,
  },
  slots: {
    lead: "col-start-1 flex items-center justify-start",
    root: "flex shrink-0 flex-col",
    row: "grid min-h-14 grid-cols-[1fr_auto_1fr] items-center gap-1",
    subtitle: "pb-2 text-center text-neutral-500 text-sm",
    title: "col-start-2 text-center font-bold text-base text-neutral-900",
    trail: "col-start-3 flex items-center justify-end",
  },
  variants: {
    grabber: {
      // px-2 sets the round nav button so its glyph aligns with the body's
      // px-5 content edge ((44px button − 20px glyph) / 2 + 8px = 20px).
      false: { root: "border-neutral-100 border-b px-2" },
      true: { root: "gap-0.5 px-5 pt-1 pb-3", row: "min-h-0", subtitle: "pb-0" },
    },
  },
});

/** Grabber pill skins. The pill is the Silk Sheet.Handle button itself (Silk
 *  hides Handle children visually); the zone div is layout only. */
export const sheetHandle = tv({
  slots: {
    pill: "shrink-0 cursor-grab touch-none rounded-full border-0 p-0 outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-offset-4 active:cursor-grabbing",
    zone: "flex w-full shrink-0 select-none items-center justify-center",
  },
  variants: {
    platform: {
      // iOS grabber: 36x5 pill in a compact zone.
      ios: { pill: "h-[5px] w-9 bg-neutral-300", zone: "h-5 pt-1.5" },
      // M3 drag handle: 32x4, 22px vertical padding for a ~48px zone.
      android: { pill: "h-1 w-8 bg-neutral-400", zone: "py-[22px]" },
      desktop: {},
    },
  },
});

// Styles a ScrollBox, which is laid out as a grid by Silk and owns its own
// overflow — no display or overflow utility applies here.
export const surfaceBody = tv({
  base: "min-h-0 flex-1 px-5 pt-5 md:px-0",
  variants: {
    centered: {
      true: "max-md:items-center max-md:justify-center max-md:pt-0",
    },
    // No footer to supply the bottom inset — mirror the footer's bottom spacing
    // (safe-area aware on mobile, 20px on desktop) so content never hugs the edge.
    footerless: {
      true: "pb-safe-[1.25rem]",
    },
  },
});

/**
 * Desktop body as a ScrollArea. Bled to the card edge (the same `-mx-5`
 * counter-move the footer makes against the panel's `px-5`) so the rail sits
 * in the card gutter rather than floating beside the content; the viewport
 * takes the padding back.
 */
export const surfaceBodyDesktop = tv({
  slots: {
    root: "-mx-5 min-h-0 flex-1",
    viewport: "px-5 pt-5",
  },
  variants: {
    // Mirrors surfaceBody's footerless bottom inset (20px on desktop).
    footerless: {
      true: { viewport: "pb-5" },
    },
  },
});

export const surfaceFooter = tv({
  base: [
    "relative flex shrink-0 items-center justify-between",
    "px-5 py-5 pb-safe-[1.25rem]",
    "transition-[border-color] duration-200",
    "md:-mx-5 md:pb-5",
  ],
});

export const surfaceFooterGradient = tv({
  base: "pointer-events-none absolute -top-3.5 right-0 left-0 h-3.5 bg-[linear-gradient(to_top,rgba(74,74,74,0.14)_0%,rgba(74,74,74,0.07)_9.6%,rgba(74,74,74,0.03)_41.8%,rgba(74,74,74,0)_100%)] transition-opacity duration-200",
  defaultVariants: {
    visible: false,
  },
  variants: {
    visible: {
      false: "opacity-0",
      true: "opacity-100",
    },
  },
});

export type SheetHeaderVariants = VariantProps<typeof sheetHeader>;
